import React, { useRef, useState } from 'react'
import styles from './styles.module.css'
import { http } from '../../../../../utils/clientHttp'
import { newIdempotencyKey } from '../../../../../utils/idempotencyKey'
import { useRegionChange } from '../../../../../hooks/useRegionChange'
import { useCartStore, selectCart } from '../../../../../store/cartStore'
import { useSessionStore } from '../../../../../store/sessionStore'
import { useRegionStore } from '../../../../../store/regionStore'
import { useRegionCountryCode, useRegionShippingInfo } from '../../../../RegionGeo'
import { useRegionOffers } from '../../../../RegionOffers'
import Text from '../../../../Text'
import InputField from './InputField'
import CtaV2 from '../../../../CtaV2'
import CountriesAutocomplete from '../../../../MyAutocomplete/CountriesAutocomplete'
import { $textPrimary } from '../../../../../styles/colors/semantics'
import ArrowRight from '../../../../icons/ArrowRight'
import inputFieldStyles from './InputField/styles.module.css'
import StatesAutocomplete from '../../../../MyAutocomplete/StatesAutocomplete'
import { CircularProgress } from '@mui/material'
import CheckmarkIcon from '../../../../icons/CheckmarkIcon'
import WarningIcon from '../../../../icons/WarningIcon'
import { $red50 } from '../../../../../styles/colors/primitives'
import { pushDataLayerEvent, pushEcommerceEvent, buildEcommercePayload } from '../../../../../utils/dataLayer'

// `regionFailure` — the invoice preview's region-class refusal (the picked country
// does not sell an item in the cart / we do not ship there), rendered under the
// Country picker because that is the control that fixes it. null when priced OK.
function AddressInput({ title, subtitle, expanded, stepNumber, onSubmit, regionFailure = null }) {
  // The in-place region change (former Overmind selectedCountryChanged) is now the
  // Zustand-backed useRegionChange hook (hooks/useRegionChange): it updates the
  // region store, refetches offers/shipping, and prunes the cart. Cart line items
  // come from the Zustand cart store (Bucket C); the checkout-session fields from
  // the Zustand session store; countries from the region store; newRegion/
  // shippingInfo/offers from the RegionGeo/RegionOffers seams.
  const { changeRegion } = useRegionChange()
  const countries = useRegionStore((s) => s.countries)
  const newRegion = useRegionCountryCode()
  const shippingInfo = useRegionShippingInfo()
  const { offers } = useRegionOffers()
  const shoppingCart = useCartStore(selectCart)
  const savedShippingAddress = useSessionStore((s) => s.shippingAddress)
  const affiliateCode = useSessionStore((s) => s.affiliateCode)
  const discountCode = useSessionStore((s) => s.discountCode)
  const setShippingAddress = useSessionStore((s) => s.setShippingAddress)
  const setPaymentStartedInfo = useSessionStore((s) => s.setPaymentStartedInfo)
  const resetDiscountCode = useSessionStore((s) => s.resetDiscountCode)

  const [shippingAddress, updateShippingAddress] = useState(savedShippingAddress || {})
  const [error, setError] = useState(null)
  const [isLoading, setIsLoading] = useState(false)

  // ── Idempotency key for the order money path ────────────────────────────────
  // Creating the order also starts the payment, and the call can outlive our 30s
  // budget on a large bundle: when it does, the backend may already have created
  // the order the customer never saw. This key identifies ONE checkout attempt and
  // is sent on every request for that attempt, so the backend replays the first
  // call's order instead of making a second one.
  //
  // A ref, NOT state and NOT a value computed in render: a re-render (a keystroke
  // in an address field, the isLoading flip) must not mint a new key, and nothing
  // here should re-render when it changes.
  //
  // Lifetime — the key stays put while the outcome is UNKNOWN, and is dropped once
  // we know it:
  //   * timeout / transport failure -> KEEP. Pressing Continue again is now safe
  //     precisely because it re-sends this key.
  //   * order created -> CLEAR. That attempt is finished; the next order the
  //     customer places must be a genuinely new one, not a replay of this one.
  //   * backend said no (bad address, item unavailable) -> CLEAR. No order exists,
  //     so there is nothing to replay, and reusing the key would make the fixed
  //     cart replay the same rejection.
  const idempotencyKeyRef = useRef(null)

  const makeOrder = () => {
    onSubmit()

    // Unchanged address == the customer is just walking forward through the steps
    // again, so don't place the order a second time.
    //
    // ...UNLESS we are still holding an idempotency key. That means the last attempt
    // for THIS address never settled (it timed out, or the connection died), and
    // this guard was then swallowing the retry outright: the address had already
    // been saved to the session before the request went out, so pressing Continue
    // again did nothing at all. Re-sending is what the customer is asking for, and
    // the key makes it safe.
    if (savedShippingAddress
        && JSON.stringify(shippingAddress) === JSON.stringify(savedShippingAddress)
        && !idempotencyKeyRef.current) {
      return
    }

    if (!idempotencyKeyRef.current) {
      idempotencyKeyRef.current = newIdempotencyKey()
    }

    setIsLoading(true)
    setError(null)

    // set to state to keep data between re-renders
    setShippingAddress(shippingAddress)
    setPaymentStartedInfo(null)

    http.post('/api/payments/makeOrderAndStartPayment', {
      shippingInfo: { address: {
        ...shippingAddress,
          country: shippingAddress.country ? shippingAddress.country.code : newRegion
      } },
      expectedShippingCountry: newRegion,
      affiliateCode: affiliateCode,
      itemsAndCount: shoppingCart,
      discountCode: discountCode
    }, { 'Idempotency-Key': idempotencyKeyRef.current })
      .then((response) => {
        if (response.data.orderKey) {
          // This attempt is settled. Drop the key so the NEXT order the customer
          // places is a genuinely new attempt rather than a replay of this one.
          idempotencyKeyRef.current = null
          const startedInvoice = response.data.paymentStartedInfo && response.data.paymentStartedInfo.invoice
          if (startedInvoice && startedInvoice.discountWarning) {
            // The discount code we sent was invalid/expired and was NOT applied to the order.
            // Stop persisting it so it isn't silently re-sent (and re-blocked) on the next checkout.
            // The warning itself is shown by InvoicePreview from paymentStartedInfo.invoice.discountWarning.
            resetDiscountCode()
          }
          setPaymentStartedInfo(response.data.paymentStartedInfo)
          const cartEntries = Object.keys(shoppingCart)
            .map(id => ({ item: offers.find(i => i.id === id), quantity: shoppingCart[id] }))
            .filter(entry => entry.item)
          pushEcommerceEvent('add_shipping_info', {
            ...buildEcommercePayload(cartEntries),
            coupon: discountCode || undefined,
            shipping_tier: shippingInfo && shippingInfo.freeShipping ? 'Free Shipping' : 'Standard Shipping',
          })
          pushDataLayerEvent('form_submit', {
            form_id: 'checkout_shipping_address',
            user_data: {
              email: shippingAddress.email,
              phone_number: shippingAddress.phone,
            },
          })
        } else if (response.data.error) {
          // The backend answered, and its answer is "no order". Nothing exists to
          // replay, so the key must not be reused — otherwise a customer who fixes
          // their address or cart and presses Continue again would be told the same
          // "no" for ever.
          idempotencyKeyRef.current = null
          setError(response.data.error)
        } else {
          idempotencyKeyRef.current = null
          // NEITHER an orderKey NOR an error. This really happens: the backend
          // answers 200 with an empty cart when an offer id in the cart is not
          // sold in the shipping region (ShoppingCart.buildFromOffersAndQuery
          // silently DROPS ids it cannot match). We used to do nothing at all
          // here, so the Continue button just re-enabled itself and checkout
          // looked dead. Always tell the customer something.
          console.error('[checkout] order response had neither orderKey nor error', response.data)
          setError({
            userCaption: 'We could not start your order',
            userMessage:
              'Something went wrong preparing your order. Please reload the page and try again, or contact support@vrs.racing if it keeps happening.',
          })
        }

        setIsLoading(false)
      })
      .catch((e) => {
        setIsLoading(false)
        // Was console.log ONLY — a failed request left the button enabled with no
        // explanation, indistinguishable from a dead page.
        console.error('[checkout] makeOrderAndStartPayment failed', e && (e.code || e.message))
        const status = e && e.response ? e.response.status : null

        // KEEP idempotencyKeyRef: we do NOT know whether the order was created, and
        // that is exactly the case the key exists for. Pressing Continue again
        // re-sends it, so the backend either replays the order it already made or
        // makes the one it never got to — but never two.
        const backendError = e && e.response && e.response.data && e.response.data.error
        if (backendError && backendError.userMessage) {
          setError(backendError)
          return
        }
        setError({
          userCaption: status === 504 ? 'This is taking longer than usual' : 'We could not start your order',
          userMessage:
            status === 504
              ? 'Our payment service did not respond in time. Press Continue to payment again — you will not be charged twice.'
              : 'We could not reach our payment service. Please check your connection and try again.',
        })
      })
  }

  if (!countries || !countries.length) {
    return (
      <form className={`${styles.wrapper} ${styles.wrapperInert}`}>
        <div className={styles.loading}>
          <CircularProgress color="inherit"/>
        </div>
      </form>
    )
  }

  if (!expanded) {
    return (
      <form className={`${styles.wrapper} ${styles.wrapperInert}`}>
        <div className={styles.titleWrapper}>
          <div className={`${styles.stepNumber} ${styles.stepNumberSuccessful}`}><CheckmarkIcon color="currentColor"/></div>
          <Text type="heading-7">{title.toUpperCase()}</Text>
        </div>
        <Text type="heading-9">Shipping address</Text>
        { renderAddressPreview(shippingAddress, countries, newRegion) }
      </form>
    )
  }

  return (
    <form
      className={isLoading ? `${styles.wrapper} ${styles.wrapperInert}` : styles.wrapper}
      onSubmit={(e) => {
        e.preventDefault()
        makeOrder()
      }}
    >
      <div className={styles.titleWrapper}>
        <div className={styles.stepNumber}><Text type="heading-9">{stepNumber}</Text></div>
        <Text type="heading-4">{title.toUpperCase()}</Text>
      </div>
      <Text type="heading-6">{subtitle}</Text>
      { renderAddressInputFields(shippingAddress, updateShippingAddress, changeRegion, countries, newRegion)}
      { regionFailure && (
        <div className={styles.error} data-testid="address-region-failure">
          <WarningIcon color={$red50}/>
          <div className={styles.errorMessage}>
            <Text type="heading-7">{regionFailureCaption(regionFailure, shippingAddress, countries, newRegion)}</Text>
            <Text type="regular">{regionFailure.message}</Text>
          </div>
        </div>
      )}
      <CtaV2
        title="Continue to payment"
        titleIcon={<ArrowRight/>}
        dark
        type="submit"
        isLoading={isLoading}
        disabled={isLoading}
        sx={{
          height: '80px',
          width: '100%',
          titleTextType: 'large'
        }}
      />
      { error && (
        <>
          <div className={styles.error}>
            <WarningIcon color={$red50}/>
            <div className={styles.errorMessage}>
              <Text type="heading-7">{error.userCaption}</Text>
              <Text type="regular">{error.userMessage}</Text>
            </div>
          </div>
        </>
      )}
    </form>
  )
}

// "Not available for delivery to Germany" — the picked country by name when we
// have it, so the customer sees WHICH choice the refusal is about.
const regionFailureCaption = (failure, address, countries, newRegion) => {
  const code = (address.country && address.country.code) || newRegion
  const country = (countries || []).find((c) => c.code === code)
  const name = country && country.name ? country.name : null
  if (failure.errorType === 'ITEMS_NOT_AVAILABLE') {
    return name ? `Not available for delivery to ${name}` : 'Not available for delivery to this country'
  }
  return name ? `We cannot ship to ${name}` : 'We cannot ship to this country'
}

const renderAddressInputFields = (address, updateAddress, changeRegion, countries, newRegion) => {
  return (
    <>
      <InputField
        labelText="Full name"
        value={address.name}
        placeholder="e.g. John Doe"
        required
        name="name"
        autoComplete="name"
        onChange={(value) => updateAddress(prev => ({ ...prev, name: value }))}
      />
      <InputField
        labelText="Email"
        value={address.email}
        type="email"
        placeholder="e.g. example@example.com"
        required
        name="email"
        autoComplete="email"
        onChange={(value) => updateAddress(prev => ({ ...prev, email: value }))}
      />
      <InputField
        labelText="Address line 1"
        value={address.line1}
        placeholder="e.g. Street name, Street number, Floor number, Apartment number"
        required
        name="address-line1"
        autoComplete="address-line1"
        onChange={(value) => updateAddress(prev => ({ ...prev, line1: value }))}
      />
      <InputField
        labelText="Address line 2"
        value={address.line2}
        placeholder="e.g. Street name, Street number, Floor number, Apartment number"
        name="address-line2"
        autoComplete="address-line2"
        onChange={(value) => updateAddress(prev => ({ ...prev, line2: value }))}
      />
      <div className={styles.row}>
        <div className={inputFieldStyles.inputWrapper}>
          <Text type="heading-9">Country</Text>
          <div className={styles.autocompleteWrapper}>
            <CountriesAutocomplete
              countries={countries}
              onChangeOverride={(newValue) => {
                updateAddress(prev => ({ ...prev, country: newValue, state: null }))
                changeRegion(newValue.code)
              }}
              selectedCountryCode={(address.country && address.country.code) || newRegion}
              inputTextColor={$textPrimary.light}
              popupIndicatorColor={$textPrimary.light}
              fontSizeAdjust={0}
              noPadding
              fullWidth
            />
          </div>
        </div>
        {
          (() => {
            const selectedCountry = address.country || countries.find(c => c.code === newRegion)
            if (selectedCountry.states && selectedCountry.states.length) {
              return (
                <div className={inputFieldStyles.inputWrapper}>
                  <Text type="heading-9">State</Text>
                  <div className={styles.autocompleteWrapper}>
                    {/* hide fake input over autocomplete to simulate default browser form error behaviour*/}
                    <input className={styles.fakeInput} value={address.state} required/>
                    <StatesAutocomplete
                      states={selectedCountry.states}
                      selectedStateCode={address.state}
                      onChange={(value) => updateAddress(prev => ({ ...prev, state: value.code }))}
                      inputTextColor={$textPrimary.light}
                      popupIndicatorColor={$textPrimary.light}
                      fontSizeAdjust={0}
                      fullWidth
                    />
                  </div>
                </div>
              )
            } else {
              // Free-text branch for countries with no state list (all of EU, AU,
              // rest of world). This field was `required`, so a German / French /
              // Polish customer who left it blank — the natural thing to do, their
              // address has no state — was stopped by the browser's native "Please
              // fill out this field" tooltip with NO console error, NO network call
              // and NO backend log. The backend only demands a state for US
              // (AddressUtils.java:97), so the frontend was stricter than the API.
              return (
                <InputField
                  labelText="State / County (optional)"
                  value={address.state}
                  placeholder="State / County"
                  name="address-level1"
                  autoComplete="address-level1"
                  onChange={(value) => updateAddress(prev => ({ ...prev, state: value }))}
                />
              )
            }
          }) ()
        }
      </div>
      <div className={styles.row}>
        <InputField
          labelText="Zip / Postal code"
          value={address.postalCode}
          placeholder="e.g. 1000"
          required
          name="postal-code"
          autoComplete="postal-code"
          onChange={(value) => updateAddress(prev => ({ ...prev, postalCode: value }))}
          onDoneTyping={(value) => useSessionStore.getState().setPostalCodeForInvoicePreview(value)}
        />
        <InputField
          labelText="City"
          value={address.city}
          placeholder="City"
          required
          name="address-level2"
          autoComplete="address-level2"
          onChange={(value) => updateAddress(prev => ({ ...prev, city: value }))}
        />
      </div>
      <InputField
        labelText="Phone number (for delivery)"
        value={address.phoneNumber}
        placeholder="e.g. +000 000000000"
        required
        name="tel"
        autoComplete="tel"
        onChange={(value) => updateAddress(prev => ({ ...prev, phoneNumber: value }))}
      />
    </>
  )
}

const renderAddressPreview = (address, countries, newRegion) => {
  const selectedCountry = address.country || countries.find(c => c.code === newRegion)

  return <div className={styles.previewTable}>
    <div className={styles.previewRow}>
      <Text type="small">Full name</Text>
      <Text type="small">{address.name}</Text>
    </div>
    <div className={styles.previewRow}>
      <Text type="small">Email</Text>
      <Text type="small">{address.email}</Text>
    </div>
    <div className={styles.previewRow}>
      <Text type="small">Address line 1</Text>
      <Text type="small">{address.line1}</Text>
    </div>
    {
      address.line2 && <div className={styles.previewRow}>
      <Text type="small">Address line 2</Text>
      <Text type="small">{address.line2}</Text>
    </div>
    }
    <div className={styles.previewRow}>
      <Text type="small">Country</Text>
      <Text type="small">{selectedCountry.name}</Text>
    </div>
    <div className={styles.previewRow}>
      <Text type="small">State</Text>
      <Text type="small">{address.state}</Text>
    </div>
    <div className={styles.previewRow}>
      <Text type="small">Zip / Postal code</Text>
      <Text type="small">{address.postalCode}</Text>
    </div>
    <div className={styles.previewRow}>
      <Text type="small">City</Text>
      <Text type="small">{address.city}</Text>
    </div>
    <div className={styles.previewRow}>
      <Text type="small">Phone number</Text>
      <Text type="small">{address.phoneNumber}</Text>
    </div>
  </div>
}

export default AddressInput
